import { motion } from "framer-motion";
import { XCircle, CheckCircle2, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import imgBefore from "@/assets/painpoint-before.jpeg";
import imgAfter from "@/assets/painpoint-after.jpeg";

function MessySpreadsheet() {
  const cells = ["Tên", "SĐT", "Lớp", "Đã đóng?", "An", "098...", "10A", "?", "Bảo", "—", "11B", "???", "Cường", "097...", "—", "x"];
  return (
    <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10/40 ring-1 ring-red-100">
      <div className="text-[9px] uppercase tracking-wider text-red-400 font-medium mb-2">Excel chồng chéo</div>
      <div className="rounded-md overflow-hidden ring-1 ring-red-100">
        <div className="grid grid-cols-4 gap-px bg-red-100">
          {cells.map((c, i) => (
            <div key={i} className={`bg-white dark:bg-slate-900 p-1.5 text-[8px] truncate ${i < 4 ? "text-slate-500 dark:text-slate-400 font-medium bg-red-50 dark:bg-red-500/10/60" : "text-slate-400 dark:text-slate-500"}`}>
              {c}
            </div>
          ))}
        </div>
      </div>
      <div className="text-[9px] text-red-500 mt-2 italic">Dễ sai · mất data · không đối soát được</div>
    </div>
  );
}

function CleanDashboard() {
  return (
    <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10/40 ring-1 ring-emerald-100">
      <div className="text-[9px] uppercase tracking-wider text-emerald-500 font-medium mb-2">Ezyclass dashboard</div>
      <div className="rounded-md bg-white dark:bg-slate-900 ring-1 ring-emerald-100 p-3 shadow-sm">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="size-5 rounded-full bg-emerald-100 grid place-items-center">
            <Check className="size-3 text-emerald-600" strokeWidth={2.5} />
          </div>
          <span className="text-[10px] font-medium text-slate-700 dark:text-slate-200">247 học viên · 12 lớp</span>
        </div>
        <div className="flex items-end gap-1 h-10 mb-2">
          {[40, 55, 45, 70, 60, 80, 75, 90].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-emerald-500/20" style={{ height: `${h}%` }}>
              <div className="w-full rounded-t bg-emerald-500" style={{ height: `${Math.min(h, 70)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-[8px] text-slate-400 dark:text-slate-500">
          <span>T1</span><span>T2</span><span>T3</span><span>T4</span><span>T5</span><span>T6</span><span>T7</span><span>T8</span>
        </div>
      </div>
      <div className="text-[9px] text-emerald-600 mt-2 italic">Rõ ràng · tự động · an toàn</div>
    </div>
  );
}

const painPoints = [
  "Điểm danh bằng sổ giấy — dễ sai, mất thời gian",
  "Giao bài qua Zalo — không theo dõi được ai nộp",
  "Báo cáo cuối tháng — ngồi cả buổi với Excel",
  "Thông báo thủ công — gửi từng phụ huynh",
  "Học phí — ghi sổ, dễ nhầm, khó đối soát",
];

const solutions = [
  "Điểm danh 1 click trên điện thoại",
  "Bài tập online — deadline + theo dõi tự động",
  "Báo cáo tự động — charts + export Excel/PDF",
  "Thông báo hàng loạt — hẹn giờ gửi",
  "Học phí tự động — nhắc đóng + ghi nhận thanh toán",
];

export function LandingPainPoints() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 20 }}
      className="bg-slate-50 dark:bg-slate-950"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Cách cũ vs. cách mới
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-400">Hai cách vận hành, một sự khác biệt rõ rệt.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="space-y-4"
          >
            <Badge className="bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200 hover:bg-red-50 dark:bg-red-500/10">Trước khi dùng Ezyclass</Badge>
            <div className="relative rounded-2xl overflow-hidden aspect-[16/10] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)]">
              <img src={imgBefore} alt="Giáo viên mệt mỏi với sổ sách thủ công" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/30 to-transparent" />
            </div>
            <MessySpreadsheet />
            <div className="space-y-3">
              {painPoints.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, type: "spring", stiffness: 100, damping: 20 }}
                  className="flex items-start gap-3 p-3 rounded-xl bg-red-50 dark:bg-red-500/10/50"
                >
                  <XCircle className="size-5 text-red-400 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{p}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="space-y-4"
          >
            <Badge className="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 hover:bg-emerald-50 dark:bg-emerald-500/10">Với Ezyclass</Badge>
            <div className="relative rounded-2xl overflow-hidden aspect-[16/10] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)]">
              <img src={imgAfter} alt="Giáo viên tự tin với Ezyclass" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent" />
            </div>
            <CleanDashboard />
            <div className="space-y-3">
              {solutions.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, type: "spring", stiffness: 100, damping: 20 }}
                  className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10/50"
                >
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 dark:text-slate-200 font-medium">{s}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
